import type { ActorId } from "src/contexts/documentationArea/domain/ActorId";
import { OrderResponseStatus } from "./OrderResponseStatus";
import { File } from "src/contexts/documentationArea/domain/File";

export type RetrievalMethodOutputUploadedBy = {
  actorId: ActorId;
  actorName: string | null;
};

export type RetrievalMethodOutput = {
  name: string;
  uploadedBy: RetrievalMethodOutputUploadedBy | null;
};

export type OrderResponseParams = {
  id: string;
  createdAt: Date;
  inputFiles: File[];
  outputFiles: File[];
  status: OrderResponseStatus;
  statusMessages: string[];
  retrievalMethodOutput: RetrievalMethodOutput | null;
};

// These messages are related to pending review, they are for the customer and not relevant to the user
const PENDING_REVIEW_MESSAGES = [
  "REVIEW_PERIOD",
  "REVIEW_EMPLOYEE_ID",
  "REVIEW_EMPLOYER_ID",
  "REVIEW_TOTAL_ACCRUED",
  "REVIEW_NET_PAY",
  "REVIEW_NET_PAY_CALCULATION",
];

export class OrderResponse {
  private readonly id: string;
  private readonly createdAt: Date;
  private readonly inputFiles: File[];
  private readonly outputFiles: File[];
  private readonly status: OrderResponseStatus;
  private readonly statusMessages: string[];
  private readonly retrievalMethodOutput: RetrievalMethodOutput | null;

  constructor({
    id,
    createdAt,
    inputFiles,
    outputFiles,
    status,
    statusMessages,
    retrievalMethodOutput,
  }: OrderResponseParams) {
    this.id = id;
    this.createdAt = createdAt;
    this.inputFiles = inputFiles;
    this.outputFiles = outputFiles;
    this.status = status;
    this.statusMessages = statusMessages;
    this.retrievalMethodOutput = retrievalMethodOutput;
  }

  public getId() {
    return this.id;
  }

  public getCreatedAt() {
    return this.createdAt;
  }

  public getInputFiles() {
    return this.inputFiles;
  }

  public getOutputFiles() {
    return this.outputFiles;
  }

  public getStatus() {
    return this.status;
  }

  public getStatusMessages() {
    // Filter out pending review messages
    const filteredMessages = this.statusMessages.filter(
      (message) => !PENDING_REVIEW_MESSAGES.includes(message),
    );
    return filteredMessages;
  }

  public isValid() {
    return this.status === OrderResponseStatus.VALID;
  }

  public isInvalid() {
    return this.status === OrderResponseStatus.INVALID;
  }

  public isPending() {
    return this.status === OrderResponseStatus.PENDING_REVIEW;
  }

  public getRetrievalMethodOutput() {
    return this.retrievalMethodOutput;
  }
}
